import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

export async function convertMarkdownToDocx(markdown: string): Promise<Blob> {
  const lines = markdown.split('\n');
  const paragraphs: Paragraph[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Empty line - add spacing
      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { after: 120 },
        })
      );
      continue;
    }

    // Simple heading detection
    if (trimmedLine.startsWith('#')) {
      const level = Math.min(trimmedLine.match(/^#+/)?.[0].length || 1, 6);
      const text = trimmedLine.replace(/^#+\s*/, '');

      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text, bold: true, size: 22 - (level - 1) * 2 })],
          heading: [
            HeadingLevel.HEADING_1,
            HeadingLevel.HEADING_2,
            HeadingLevel.HEADING_3,
            HeadingLevel.HEADING_4,
            HeadingLevel.HEADING_5,
            HeadingLevel.HEADING_6,
          ][level - 1],
          spacing: { before: 200, after: 200 },
        })
      );
      continue;
    }

    // Basic bullet list detection (unordered lists only)
    if (trimmedLine.match(/^(\s*)[-*+]\s/)) {
      const leadingSpaces = line.length - trimmedLine.length;
      const depth = Math.floor(leadingSpaces / 2);
      const text = trimmedLine.replace(/^(\s*)[-*+]\s*/, '');

      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text })],
          bullet: { level: depth },
          indent: { left: (depth + 1) * 720 }, // 720 TWIPs = 0.5 inches per level
          spacing: { before: 120, after: 120 },
        })
      );
      continue;
    }

    // Everything else is just a regular paragraph
    paragraphs.push(
      new Paragraph({
        children: [new TextRun({ text: trimmedLine })],
        spacing: { before: 120, after: 120 },
      })
    );
  }

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: 'Arial', // Eva requested Arial 11pt
            size: 22, // 11pt (docx uses half-points)
          },
          paragraph: {
            alignment: AlignmentType.LEFT,
          },
        },
      },
    },
    sections: [{ children: paragraphs }],
  });

  return Packer.toBlob(doc);
}

export function downloadDocx(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
